<!--
  Roles.svelte — Admin CRUD page for managing roles.

  Lists all roles in a table with create/edit modal.
  Supports toggling active state, linking to applications, and deletion.
  Only accessible to admin users.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import {
		Table,
		TableHead,
		TableHeadCell,
		TableBody,
		TableBodyRow,
		TableBodyCell,
		Badge,
		Button,
		Input,
		Spinner,
		Toggle,
		Modal,
		Label,
		Textarea
	} from 'flowbite-svelte';
	import PageHeader from '../components/PageHeader.svelte';
	import AbstractDropdown from '../components/AbstractDropdown.svelte';
	import { ConfirmButton, Panel, SortableTableHeadCell } from '../components';
	import { listRoles, createRole, updateRole, deleteRole, listApplications } from '../AppNetwork';
	import type { Role, Application, SortDirection } from '../AppTypes';
	import { toastSuccess, toastError } from '../toast';
	import { t, t2 } from '../i18n';

	/** All roles loaded from the server. */
	let roles: Role[] = $state([]);
	/** All applications (for the application dropdown). */
	let applications: Application[] = $state([]);
	/** Whether the initial load is in progress. */
	let loading = $state(true);
	/** Error banner text. */
	let error = $state('');
	/** Search/filter text for the table. */
	let search = $state('');
	/** Status filter: '' = all, 'active', 'inactive'. */
	let filterStatus = $state('');
	/** Application filter: '' = all, 'global' = no app, or numeric app ID. */
	let filterAppId = $state('');

	// ── Sorting ───────────────────────────────────────────────────

	/** Active server-side sort key (`''` = server default, name ascending). */
	let sortBy = $state('');
	/** Active server-side sort direction. */
	let sortDir: SortDirection = $state('asc');

	/** Switch the sort column/direction and refetch from the server. */
	function handleSort(key: string, dir: SortDirection): void {
		sortBy = key;
		sortDir = dir;
		loadData();
	}

	// ── Modal state ───────────────────────────────────────────────

	/** Whether the create/edit modal is open. */
	let showModal = $state(false);
	/** The role being edited, or null for create mode. */
	let editing: Role | null = $state(null);
	/** Whether the modal save action is in progress. */
	let saving = $state(false);

	// ── Form fields ───────────────────────────────────────────────

	let formName = $state('');
	let formDescription = $state('');
	let formIsActive = $state(true);
	let formApplicationID: string = $state('');

	// ── Delete ────────────────────────────────────────────────────

	/** Execute the deletion of a role. */
	async function handleDelete(role: Role): Promise<void> {
		try {
			await deleteRole(role.id);
			toastSuccess(t2('Role "{{name}}" deleted.', { name: role.name }));
			await loadData();
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to delete role'));
		}
	}

	// ── Data loading ──────────────────────────────────────────────

	/** Fetch all roles and applications from the server. */
	async function loadData(): Promise<void> {
		loading = true;
		error = '';
		/** Fetch roles and applications concurrently, capturing any failure */
		/** Fetch roles and applications concurrently, capturing any failure */
		try {
			const [r, a] = await Promise.all([
				listRoles({ sortBy: sortBy || undefined, sortDir: sortBy ? sortDir : undefined }),
				listApplications()
			]);
			roles = r;
			applications = a;
		} catch (e) {
			error = e instanceof Error ? e.message : t2('Failed to load roles');
		} finally {
			loading = false;
		}
	}

	// ── Filtered list ─────────────────────────────────────────────

	/** Whether any non-default filter is active. */
	let hasFilters = $derived(!!search || !!filterStatus || !!filterAppId);

	let filtered = $derived(roles.filter((r) => {
		if (search) {
			const q = search.toLowerCase();
			if (!r.name.toLowerCase().includes(q) && !(r.description || '').toLowerCase().includes(q)) return false;
		}
		/** Exclude roles that don't match the active-status filter */
		if (filterStatus === 'active' && !r.isActive) return false;
		/** Exclude roles that don't match the inactive-status filter */
		if (filterStatus === 'inactive' && r.isActive) return false;
		/** Exclude app-scoped roles when showing global-only */
		if (filterAppId === 'global' && r.applicationID) return false;
		/** Exclude roles not belonging to the selected application */
		if (filterAppId && filterAppId !== 'global' && r.applicationID !== parseInt(filterAppId)) return false;
		return true;
	}));

	/** Clear all filters to defaults. */
	function clearFilters(): void {
		search = '';
		filterStatus = '';
		filterAppId = '';
	}

	/** Build application select options. */
	let appOptions = $derived([
		{ value: '', name: t2('None (Global)'), icon: 'fa-solid fa-globe' },
		...applications.map((a) => ({ value: String(a.id), name: a.applicationName, icon: 'fa-solid fa-cube' }))
	]);

	/** Map application IDs to names for table display. */
	let appMap = $derived(new Map(applications.map((a) => [a.id, a.applicationName])));

	/** Status filter dropdown items. */
	const statusFilterOptions = [
		{ value: '', name: t2('All statuses'), icon: 'fa-solid fa-list' },
		{ value: 'active', name: t2('Active'), icon: 'fa-solid fa-circle-check' },
		{ value: 'inactive', name: t2('Inactive'), icon: 'fa-solid fa-circle-xmark' }
	];

	/** App filter dropdown items. */
	let appFilterOptions = $derived([
		{ value: '', name: t2('All apps'), icon: 'fa-solid fa-list' },
		{ value: 'global', name: t2('Global only'), icon: 'fa-solid fa-globe' },
		...applications.map((a) => ({ value: String(a.id), name: a.applicationName, icon: 'fa-solid fa-cube' }))
	]);

	// ── Modal helpers ─────────────────────────────────────────────

	/** Open modal in create mode. */
	function openCreate(): void {
		editing = null;
		formName = '';
		formDescription = '';
		formIsActive = true;
		formApplicationID = '';
		showModal = true;
	}

	/** Open modal in edit mode. */
	function openEdit(role: Role): void {
		editing = role;
		formName = role.name;
		formDescription = role.description || '';
		formIsActive = role.isActive;
		formApplicationID = role.applicationID ? String(role.applicationID) : '';
		showModal = true;
	}

	/** Save (create or update) the role. */
	async function handleSave(): Promise<void> {
		if (!formName.trim()) {
			error = t2('Role name is required.');
			return;
		}

		saving = true;
		error = '';

		const payload: Partial<Role> = {
			name: formName.trim(),
			description: formDescription || null,
			isActive: formIsActive,
			applicationID: formApplicationID ? parseInt(formApplicationID) : null
		};

		/** Persist the role via create or update, then refresh the list */
		try {
			if (editing) {
				await updateRole(editing.id, payload);
			} else {
				await createRole(payload);
			}
			showModal = false;
			editing = null;
			await loadData();
		} catch (e) {
			error = e instanceof Error ? e.message : t2('Failed to save role');
		} finally {
			saving = false;
		}
	}

	/** Format a date string for display. */
	function formatDate(dateString: string): string {
		return new Date(dateString).toLocaleDateString(undefined, {
			year: 'numeric',
			month: 'short',
			day: 'numeric'
		});
	}

	onMount(loadData);
</script>

<PageHeader title={$t("Roles")} description={$t("Define access roles and assign them to users or applications.")} />

<!-- Filters -->
<Panel title={$t("Filters")} faIcon="filter" class="mb-6">
	<div class="flex flex-wrap items-center gap-3">
		<div class="max-w-xs flex-1">
			<Input bind:value={search} placeholder={$t("Search roles...")}>
				{#snippet left()}
							<i  class="fa-solid fa-search text-gray-400"></i>
						{/snippet}
			</Input>
		</div>
		<AbstractDropdown items={statusFilterOptions} bind:value={filterStatus} class="w-44" />
		<AbstractDropdown items={appFilterOptions} bind:value={filterAppId} class="w-44" />
		{#if hasFilters}
			<Button size="sm" color="alternative" onclick={clearFilters}>
				<i class="fa-solid fa-xmark me-1"></i>{$t("Clear")}
			</Button>
		{/if}
		<Button onclick={openCreate}>
			<i class="fa-solid fa-plus me-2"></i>{$t("New Role")}
		</Button>
	</div>
</Panel>

{#if loading}
	<div class="flex justify-center py-12">
		<Spinner size="8" />
	</div>
{:else if error && roles.length === 0}
	<div class="py-12 text-center">
		<i class="fa-solid fa-exclamation-triangle mb-4 text-4xl text-red-500"></i>
		<p class="text-gray-500 dark:text-gray-400">{error}</p>
		<Button class="mt-4" onclick={loadData}>{$t("Retry")}</Button>
	</div>
{:else if filtered.length === 0}
	<div class="py-12 text-center">
		<i class="fa-solid fa-shield-halved mb-4 text-4xl text-gray-300 dark:text-gray-600"></i>
		<p class="text-gray-500 dark:text-gray-400">
			{hasFilters ? $t('No roles match your filters') : $t('No roles defined yet')}
		</p>
	</div>
{:else}
	{#if error}
		<div class="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
			<i class="fa-solid fa-exclamation-triangle me-2"></i>{error}
		</div>
	{/if}

	<Table divClass="widget !p-1 relative" class="dataTable">
		<TableHead class="bg-gray-50 dark:bg-gray-700">
			<SortableTableHeadCell key="name" {sortBy} {sortDir} onsort={handleSort}>{$t("Role")}</SortableTableHeadCell>
			<TableHeadCell>{$t("UUID")}</TableHeadCell>
			<SortableTableHeadCell key="application" {sortBy} {sortDir} onsort={handleSort}>
				{$t("Application")}
			</SortableTableHeadCell>
			<SortableTableHeadCell key="status" {sortBy} {sortDir} onsort={handleSort}>{$t("Status")}</SortableTableHeadCell>
			<SortableTableHeadCell key="created" {sortBy} {sortDir} defaultDir="desc" onsort={handleSort}>
				{$t("Created")}
			</SortableTableHeadCell>
			<TableHeadCell>{$t("Actions")}</TableHeadCell>
		</TableHead>
		<TableBody class="divide-y divide-gray-200 dark:divide-gray-700">
			{#each filtered as role (role.id)}
				<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
					<TableBodyCell class="font-medium text-gray-900 dark:text-white">
						<div>
							<div class="flex items-center gap-2">
								<i class="fa-solid fa-shield-halved text-primary-500"></i>
								{role.name}
							</div>
							{#if role.description}
								<div class="max-w-[200px] truncate text-xs text-gray-500 dark:text-gray-400">
									{role.description}
								</div>
							{/if}
						</div>
					</TableBodyCell>
					<TableBodyCell>
						<code class="rounded bg-gray-100 px-1.5 py-0.5 text-xs dark:bg-gray-800"
							>{role.roleUUID.substring(0, 8)}…</code
						>
					</TableBodyCell>
					<TableBodyCell>
						{#if role.applicationID}
							<Badge color="indigo"
								>{appMap.get(role.applicationID) || `App #${role.applicationID}`}</Badge
							>
						{:else}
							<span class="text-sm text-gray-400">{$t("Global")}</span>
						{/if}
					</TableBodyCell>
					<TableBodyCell>
						{#if role.isActive}
							<Badge color="green">{$t("Active")}</Badge>
						{:else}
							<Badge color="red">{$t("Inactive")}</Badge>
						{/if}
					</TableBodyCell>
					<TableBodyCell class="text-gray-500 dark:text-gray-400">
						{formatDate(role.created)}
					</TableBodyCell>
					<TableBodyCell>
						<div class="flex gap-1">
							<Button size="xs" color="alternative" onclick={() => openEdit(role)}>
								<i class="fa-solid fa-pen-to-square mr-1 h-3 w-3"></i>{$t("Edit")}
							</Button>
							<ConfirmButton
								size="xs"
								color="red"
								outline
								action={$t("delete this role")}
								onconfirm={() => handleDelete(role)}
							>
								<i class="fa-solid fa-trash text-xs"></i>
							</ConfirmButton>
						</div>
					</TableBodyCell>
				</TableBodyRow>
			{/each}
		</TableBody>
	</Table>
{/if}

<!-- Create/Edit Modal -->
<Modal bind:open={showModal} title={editing ? $t('Edit Role') : $t('New Role')} size="md" autoclose={false}>
	<div class="space-y-5">
		<div>
			<Label class="mb-1">{$t("Role Name")} *</Label>
			<Input bind:value={formName} placeholder={$t("e.g. admin, editor, viewer")} />
		</div>

		<div>
			<Label class="mb-1">{$t("Description")}</Label>
			<Textarea bind:value={formDescription} rows={2} placeholder={$t("What this role grants access to...")} class="w-full" />
		</div>

		<div>
			<Label class="mb-1">{$t("Scoped to Application")}</Label>
			<AbstractDropdown items={appOptions} bind:value={formApplicationID} onchange={(v) => { formApplicationID = String(v); }} class="w-full" />
			<p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
				{$t('Leave as "None" for a global role that applies across all applications.')}
			</p>
		</div>

		<div class="flex flex-wrap gap-6 border-t border-gray-200 pt-2 dark:border-gray-700">
			<Toggle bind:checked={formIsActive}>{$t("Active")}</Toggle>
		</div>

		{#if editing}
			<div class="border-t border-gray-200 pt-2 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
				<p><strong>{$t("UUID")}:</strong> <code class="select-all">{editing.roleUUID}</code></p>
				<p><strong>{$t("Created")}:</strong> {formatDate(editing.created)}</p>
			</div>
		{/if}
	</div>

	{#snippet footer()}
	
			<div class="flex gap-2">
				<Button color="primary" onclick={handleSave} disabled={saving}>
					{#if saving}<Spinner size="4" class="mr-2" />{/if}
					{editing ? $t('Save Changes') : $t('Create Role')}
				</Button>
				<Button color="alternative" onclick={() => (showModal = false)}>{$t("Cancel")}</Button>
			</div>
		
	{/snippet}
</Modal>

<!-- Delete confirmation is now handled inline by ConfirmButton -->
